const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const API_CONFIG = {
  baseUrl: BASE_URL,

  endpoints: {
    // Health
    healthcheck: '/healthcheck/liveness',

    // Auth
    auth: {
      login: '/auth/login',
      loginApp: '/auth/login-app',
      register: '/auth/register',
      profile: '/auth/profile',
      logout: '/auth/logout',
      changePassword: '/auth/change-password',
      firstChangePassword: '/auth/first-change-password',
      changeProject: '/auth/change-project-access',
    },

    // Users
    users: {
      list: '/user',
      create: '/user',
      byId: (id: string) => `/user/${id}`,
      update: (id: string) => `/user/${id}`,
      delete: (id: string) => `/user/${id}`,
    },

    // Short URLs (reset-password links, shared invoices…)
    shortUrl: {
      byCode: (code: string) => `/short-url/${code}`,
      create: '/short-url',
      generate: '/short-url/generate',
      resetPassword: (userId: string) => `/short-url/reset-password/${userId}`,
      expire: (code: string) => `/short-url/${code}/expire`,
    },

    // Projects
    projects: {
      list: '/project',
      setting: '/project/setting',
      byCode: (code: string) => `/project/${code}`,
      byId: (id: string) => `/project/id/${id}`,
      create: '/project',
      updateSetting: '/project/setting',
      update: (id: string) => `/project/${id}`,
      createSchedule: '/project/schedule',
      schedules: (id: string) => `/project/schedule/${id}`,
    },

    // Products
    products: {
      list: '/product',
      publicList: '/product/public',
      create: '/product',
      byId: (id: string) => `/product/${id}`,
      update: (id: string) => `/product/${id}`,
      delete: (id: string) => `/product/${id}`,
      config: (id: string) => `/product/${id}/config`,
      categories: '/product/category',
      categoryById: (id: string) => `/product/category/${id}`,
      orderList: '/product/order',
    },

    // Customers
    customers: {
      list: '/customer',
      adminList: '/customer/admin',
      create: '/customer',
      byId: (id: string) => `/customer/${id}`,
      update: (id: string) => `/customer/${id}`,
    },

    // Services
    services: {
      list: '/service',
      create: '/service',
      byId: (id: string) => `/service/${id}`,
      update: (id: string) => `/service/${id}`,
      delete: (id: string) => `/service/${id}`,
      duplicate: (id: string) => `/service/${id}/duplicate`,
      updateSequence: (id: string) => `/service/${id}/sequence`,
      types: '/service/types',
      createType: '/service/type',
      updateType: (id: string) => `/service/type/${id}`,
      deleteType: (id: string) => `/service/type/${id}`,
      bookings: '/service/booking',
      createBooking: '/service/booking',
      bookingById: (id: string) => `/service/booking/${id}`,
      updateBooking: (id: string) => `/service/booking/${id}`,
      recentBookings: '/service/recent-booking',
      bookingSummary: '/service/booking/summaryTodayTomorrowThisWeek',
      attendedBy: '/service/attended-by',
      customForms: '/service/custom-form',
      createCustomForm: '/service/custom-form',
      updateCustomForm: (id: string) => `/service/custom-form/${id}`,
      deleteCustomForm: (id: string) => `/service/custom-form/${id}`,
    },

    // Services v2
    servicesV2: {
      bookings: '/v2/service/booking',
      createBooking: '/v2/service/booking',
      updateBooking: (id: string) => `/v2/service/booking/${id}`,
      updateBookingStatus: (id: string) => `/v2/service/booking/${id}/status`,
      recentBookings: '/v2/service/recent-booking',
      monthlySales: '/v2/service/booking/sales/monthly',
    },

    // Services v3
    servicesV3: {
      bookings: '/v3/service/booking',
      recentBookings: '/v3/service/recent-booking',
    },

    // Files
    files: {
      upload: '/file/upload',
      list: '/file/list',
      detail: (id: string) => `/file/detail/${id}`,
      byId: (id: string) => `/file/${id}`,
    },

    // Cart
    cart: {
      list: '/cart',
      add: '/cart',
      updateItem: (id: string) => `/cart/${id}/item`,
      deleteItem: (id: string) => `/cart/${id}/item`,
      total: (userId: string) => `/cart/${userId}/total`,
      checkout: '/cart/checkout',
    },

    // Orders
    orders: {
      list: '/order',
      create: '/order',
      byId: (id: string) => `/order/${id}`,
      update: (id: string) => `/order/${id}`,
      delete: (id: string) => `/order/${id}`,
      history: '/order/history',
      checkout: '/order/checkout',
      custom: '/order/custom',
    },

    // Invoices
    invoices: {
      list: '/invoice',
      create: '/invoice',
      byId: (id: string) => `/invoice/${id}`,
      update: (id: string) => `/invoice/${id}`,
      delete: (id: string) => `/invoice/${id}`,
      byCustomer: (id: string) => `/invoice/customer/${id}`,
    },

    // Settings
    settings: {
      list: '/setting',
      create: '/setting',
      byType: (type: string) => `/setting/${type}`,
    },

    // WhatsApp notification templates
    whatsapp: {
      list: '/setting/whatsapp',
      catalogue: '/setting/whatsapp/catalogue',
      byCode: (code: string) => `/setting/whatsapp/${code}`,
    },

    // Role access
    roleAccess: {
      list: '/role-access',
      create: '/role-access',
      update: (id: string) => `/role-access/${id}`,
      delete: (id: string) => `/role-access/${id}`,
    },

    // Notifications
    notifications: {
      list: '/notification/list',
      listByType: (type: string) => `/notification/list/${type}`,
      create: '/notification/create',
      markAsRead: '/notification/mark-as-read',
      delete: (id: string) => `/notification/${id}`,
      sendBatch: '/notification/send-batch',
      contactUs: '/notification/contact-us',
    },

    // Commissions
    commissions: {
      monthly: '/commission/monthly',
      monthlyBreakdown: '/commission/monthly/breakdown',
    },

    // Rewards
    rewards: {
      user: '/rewards/user',
      points: '/rewards/points',
      pointsById: (id: string) => `/rewards/points/${id}`,
    },

    // Campaigns
    campaigns: {
      list: '/campaign',
      detail: (id: string) => `/campaign/detail/${id}`,
      create: '/campaign/create',
      update: (id: string) => `/campaign/update/${id}`,
      delete: (id: string) => `/campaign/delete/${id}`,
      ranking: (id: string) => `/campaign/${id}/ranking`,
    },

    // Promos
    promos: {
      list: '/promo/list',
    },

    // Expenses
    expenses: {
      list: '/expenses',
      create: '/expenses',
      byId: (id: string) => `/expenses/${id}`,
      update: (id: string) => `/expenses/${id}`,
      delete: (id: string) => `/expenses/${id}`,
    },

    // Shifts
    shifts: {
      list: '/shift',
      create: '/shift',
      active: '/shift/active',
      byId: (id: string) => `/shift/${id}`,
      update: (id: string) => `/shift/${id}`,
      updateStatus: (id: string) => `/shift/${id}/status`,
      end: (id: string) => `/shift/${id}/end`,
    },

    // Discounts
    discounts: {
      list: '/discount',
      create: '/discount',
      active: '/discount/active',
      byCode: (code: string) => `/discount/code/${code}`,
      byId: (id: string) => `/discount/${id}`,
      update: (id: string) => `/discount/${id}`,
      updateStatus: (id: string) => `/discount/${id}/status`,
      delete: (id: string) => `/discount/${id}`,
    },

    // Wallet
    wallet: {
      base: '/wallet',
    },

    // Delivery
    delivery: {
      list: '/delivery',
      create: '/delivery',
      byId: (id: string) => `/delivery/${id}`,
      update: (id: string) => `/delivery/${id}`,
      delete: (id: string) => `/delivery/${id}`,
    },
  },
} as const;
